import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { PortfolioMilestoneFilterService } from 'app/services/portfolio-milestone-filter/portfolio-milestone-filter.service';
import { Subject, takeUntil } from 'rxjs';

type FilterList = Record<string, Set<string>>;

@Component({
  selector: 'header-menu-milestone',
  templateUrl: './header-menu-milestone.component.html',
  styleUrls: ['./header-menu-milestone.component.scss']
})
export class HeaderMenuMilestoneComponent implements OnChanges, OnDestroy, OnInit {
  private milestoneFilterService = inject(PortfolioMilestoneFilterService);
  private destroy$ = new Subject<void>();
  private static sortedFiltersCache = new WeakMap<Set<string>, string[]>();

  @ViewChild('menuTrigger', { static: false }) menuTrigger!: MatMenuTrigger;

  @Input() field = '';
  @Input() showHeaderMenu = false;
  @Input() columnName = '';
  @Input() currentSortColumn: string | null = null;
  @Input() currentSortDirection: 'asc' | 'desc' | null = null;

  @Output() focusOut = new EventEmitter<boolean>();
  @Output() sortRequested = new EventEmitter<'asc' | 'desc'>();
  @Output() menuItemClicked = new EventEmitter<string>();

  activeMenuColumn: string | null = null;
  availableFilters: string[] = [];
  appliedFilters: string[] = [];
  tempSelectedFilters = new Set<string>(); // Temporary selection state

  get filterOptions(): FilterList {
    return this.milestoneFilterService.getCurrentState().availableOptions;
  }

  get filtersApplied(): FilterList {
    if (this.isSharedStatusFilter) {
      return this.milestoneFilterService.getDisplayFilters();
    }

    return this.milestoneFilterService.getFiltersForSource('table');
  }

  ngOnInit() {
    this.milestoneFilterService.filterState$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateLocalFilterState();
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges() {
    this.updateLocalFilterState();
  }

  private updateLocalFilterState() {
    const appliedFiltersSet = new Set([...(this.filtersApplied[this.field] || [])]);
    this.appliedFilters = Array.from(appliedFiltersSet);
    this.tempSelectedFilters = new Set(this.appliedFilters);

    if (!this.menuTrigger?.menuOpen) {
      return;
    }

    this.refreshAvailableFilters();
  }

  onFilterClear() {
    this.tempSelectedFilters.clear();

    if (this.isSharedStatusFilter) {
      this.milestoneFilterService.clearFieldFilters(this.field, 'table');
      this.milestoneFilterService.clearFieldFilters(this.field, 'chart');
    } else {
      this.milestoneFilterService.clearFieldFilters(this.field, 'table');
    }

    if (this.menuTrigger) {
      this.menuTrigger.closeMenu();
    }
  }

  onFilterApply() {
    const currentFilters = { ...this.milestoneFilterService.getFiltersForSource('table') };
    if (this.tempSelectedFilters.size > 0) {
      currentFilters[this.field] = new Set(this.tempSelectedFilters);
    } else {
      delete currentFilters[this.field];
    }

    this.milestoneFilterService.setFilters(currentFilters, 'table');

    if (this.isSharedStatusFilter) {
      const chartFilters = { ...this.milestoneFilterService.getFiltersForSource('chart') };
      if (this.tempSelectedFilters.size > 0) {
        chartFilters[this.field] = new Set(this.tempSelectedFilters);
      } else {
        delete chartFilters[this.field];
      }
      this.milestoneFilterService.setFilters(chartFilters, 'chart');
    }

    if (this.menuTrigger) {
      this.menuTrigger.closeMenu();
    }
  }

  onHeaderMenuOpened(): void {
    this.activeMenuColumn = this.columnName;
    this.refreshAvailableFilters();
  }

  onHeaderMenuClosed(): void {
    this.activeMenuColumn = null;
  }

  onMenuItemClick(direction: 'asc' | 'desc') {
    this.sortRequested.emit(direction);
  }

  onFilterToggle(filter: string, event: any) {
    const isChecked = event.checked;

    if (isChecked) {
      this.tempSelectedFilters.add(filter);
    } else {
      this.tempSelectedFilters.delete(filter);
    }
  }

  isFilterApplied(filter: string): boolean {
    return this.appliedFilters.includes(filter);
  }

  isFilterSelected(filter: string): boolean {
    return this.tempSelectedFilters.has(filter);
  }

  get appliedFiltersCount(): number {
    return this.appliedFilters.length;
  }

  get selectedFiltersCount(): number {
    return this.tempSelectedFilters.size;
  }

  trackByFilter(index: number, filter: string): string {
    return filter;
  }

  private refreshAvailableFilters(): void {
    const availableFilterSet = this.filterOptions[this.field] || new Set<string>();
    this.availableFilters = this.getSortedFilterValues(availableFilterSet);
    if (this.appliedFilters.length > 0) {
      this.appliedFilters = this.appliedFilters.filter((filter) => availableFilterSet.has(filter));
      this.tempSelectedFilters = new Set(this.appliedFilters);
    }
  }

  private sortFilterValues(filters: string[]): string[] {
    // Simple alphabetical sort for milestone filters
    return [...filters].sort((a, b) => a.localeCompare(b));
  }

  private getSortedFilterValues(filters: Set<string>): string[] {
    const cached = HeaderMenuMilestoneComponent.sortedFiltersCache.get(filters);
    if (cached) {
      return cached;
    }

    const sortedFilters = this.sortFilterValues(Array.from(filters));
    HeaderMenuMilestoneComponent.sortedFiltersCache.set(filters, sortedFilters);
    return sortedFilters;
  }

  private get isSharedStatusFilter(): boolean {
    return this.field === 'status';
  }
}
